import * as THREE from "three";

/**
 * Stateful grab handle for a scene object.
 *
 * Rest pose is captured in the object's initial parent space so reset/drop can
 * restore without teleport jumps. Ownership is tracked by GrabManager — this
 * type only describes grab eligibility and attach bookkeeping.
 */
export interface Grabbable {
  object: THREE.Object3D;
  /** Stable id for debugging / locking maps (e.g. wire-hot, receptacle). */
  id: string;
  /** When false, grab attempts are ignored (locked placement or gated). */
  enabled: boolean;
  locked: boolean;
  /** Original parent at registration (usually a station group). */
  restParent: THREE.Object3D;
  restPosition: THREE.Vector3;
  restQuaternion: THREE.Quaternion;
  /** Optional tip/anchor used for placement distance (wires). */
  placeAnchor: THREE.Object3D;
  /** Highlight materials for hover / lock / reject (may be empty). */
  feedbackMats: THREE.MeshStandardMaterial[];
  /**
   * When true, a locked object can be grabbed: occupancy is cleared and the
   * object is held (unplug). Outlet wires leave this false so seated leads stay
   * until trainer reset.
   */
  unplugOnGrab: boolean;
}

export interface CreateGrabbableOptions {
  id: string;
  object: THREE.Object3D;
  /** Defaults to the object itself. */
  placeAnchor?: THREE.Object3D;
  enabled?: boolean;
  /** Default false — locked placements stay seated until restore. */
  unplugOnGrab?: boolean;
}

/** Capture rest pose and collect standard-material meshes for feedback. */
export function createGrabbable(opts: CreateGrabbableOptions): Grabbable {
  const { object } = opts;
  if (!object.parent) {
    throw new Error(`[grabbable] ${opts.id} must be parented before registration`);
  }

  const feedbackMats: THREE.MeshStandardMaterial[] = [];
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (m instanceof THREE.MeshStandardMaterial) feedbackMats.push(m);
    }
  });

  return {
    object,
    id: opts.id,
    enabled: opts.enabled ?? true,
    locked: false,
    restParent: object.parent,
    restPosition: object.position.clone(),
    restQuaternion: object.quaternion.clone(),
    placeAnchor: opts.placeAnchor ?? object,
    feedbackMats,
    unplugOnGrab: opts.unplugOnGrab ?? false,
  };
}

/** Restore object to captured rest pose and clear lock. */
export function restoreGrabbable(g: Grabbable, enabled = true): void {
  g.locked = false;
  g.enabled = enabled;
  g.restParent.attach(g.object);
  g.object.position.copy(g.restPosition);
  g.object.quaternion.copy(g.restQuaternion);
  g.object.scale.set(1, 1, 1);
}
